import Oystehr from '@oystehr/sdk';
import { Appointment, CodeableConcept, Consent, DocumentReference, Encounter } from 'fhir/r4b';
import { CODE_SYSTEM_SERVICE_CATEGORY_CODES } from '../helpers/rcm/constants';
import { AppointmentAttendanceType, AppointmentType } from '../types/api/appointment.types';
import { REASON_FOR_VISIT_SEPARATOR, TELEMED_VIDEO_ROOM_CODE } from '../types/constants';
import { EncounterVirtualServiceExtension } from '../types/data/oystehr-api.types.ts/telemed.types';
import { PAPERWORK_CONSENT_CODE_UNIQUE } from '../types/data/paperwork/paperwork.constants';
import {
  FHIR_APPOINTMENT_TYPE_MAP,
  FHIR_ZAPEHR_URL,
  PUBLIC_EXTENSION_BASE_URL,
  ROOM_EXTENSION_URL,
  SERVICE_CATEGORY_SYSTEM,
} from './constants';
import { getCoding } from './helpers';
import { OTTEHR_MODULE } from './moduleIdentification';

export function getAppointmentRoom(appointment: Appointment): string | undefined {
  return appointment.extension?.find((ext) => ext.url === ROOM_EXTENSION_URL)?.valueString;
}

export async function updateAppointmentRoom(
  appointment: Appointment,
  room: string | undefined,
  oystehr: Oystehr
): Promise<Appointment> {
  if (!appointment.id) {
    throw new Error('Appointment resource missing id');
  }

  const existingExtension = appointment.extension ?? [];

  if (!room) {
    const updatedExtension = existingExtension.filter((ext) => ext.url !== ROOM_EXTENSION_URL);
    if (updatedExtension.length === existingExtension.length) {
      return appointment;
    }
    if (updatedExtension.length === 0) {
      return oystehr.fhir.patch<Appointment>({
        resourceType: 'Appointment',
        id: appointment.id,
        operations: [{ op: 'remove', path: '/extension' }],
      });
    }
    return oystehr.fhir.patch<Appointment>({
      resourceType: 'Appointment',
      id: appointment.id,
      operations: [{ op: 'replace', path: '/extension', value: updatedExtension }],
    });
  }

  const roomExtensionIndex = existingExtension.findIndex((ext) => ext.url === ROOM_EXTENSION_URL);

  if (roomExtensionIndex !== -1) {
    const updatedExtension = [...existingExtension];
    updatedExtension[roomExtensionIndex] = { url: ROOM_EXTENSION_URL, valueString: room };
    return oystehr.fhir.patch<Appointment>({
      resourceType: 'Appointment',
      id: appointment.id,
      operations: [{ op: 'replace', path: '/extension', value: updatedExtension }],
    });
  }

  if (existingExtension.length === 0) {
    return oystehr.fhir.patch<Appointment>({
      resourceType: 'Appointment',
      id: appointment.id,
      operations: [{ op: 'add', path: '/extension', value: [{ url: ROOM_EXTENSION_URL, valueString: room }] }],
    });
  }

  const updatedExtension = [...existingExtension];
  updatedExtension.push({ url: ROOM_EXTENSION_URL, valueString: room });
  return oystehr.fhir.patch<Appointment>({
    resourceType: 'Appointment',
    id: appointment.id,
    operations: [{ op: 'replace', path: '/extension', value: updatedExtension }],
  });
}

export async function cancelAppointmentResource(
  appointment: Appointment,
  cancellationReasonCoding: NonNullable<CodeableConcept['coding']>,
  oystehr: Oystehr
): Promise<Appointment> {
  if (!appointment.id) {
    throw Error('Appointment resource missing id');
  }

  try {
    const response: Appointment = await oystehr.fhir.patch({
      resourceType: 'Appointment',
      id: appointment.id,
      operations: [
        {
          op: 'replace',
          path: '/status',
          value: 'cancelled',
        },
        {
          op: 'add',
          path: '/cancelationReason',
          value: {
            coding: cancellationReasonCoding,
          },
        },
      ],
    });
    return response;
  } catch (error: unknown) {
    throw new Error(`Failed to cancel Appointment: ${JSON.stringify(error)}`);
  }
}

export async function getAppointmentResourceById(
  appointmentID: string,
  oystehr: Oystehr
): Promise<Appointment | undefined> {
  let response: Appointment | null = null;
  try {
    response = await oystehr.fhir.get<Appointment>({
      resourceType: 'Appointment',
      id: appointmentID,
    });
  } catch (error: any) {
    if (error?.issue?.[0]?.code === 'not-found') {
      return undefined;
    } else {
      throw error;
    }
  }

  return response;
}

export const getVirtualServiceResourceExtension = (
  resource: Appointment | Encounter,
  code: typeof TELEMED_VIDEO_ROOM_CODE | 'twilio-conversations'
): EncounterVirtualServiceExtension | null => {
  let resourcePrefix: string;
  if (resource.resourceType === 'Appointment') {
    resourcePrefix = 'appointment';
  } else if (resource.resourceType === 'Encounter') {
    resourcePrefix = 'encounter';
  } else {
    return null;
  }

  for (let index = 0; index < (resource.extension?.length ?? 0); index++) {
    const extension = resource.extension?.[index];
    if (extension?.url !== `${PUBLIC_EXTENSION_BASE_URL}/${resourcePrefix}-virtual-service-pre-release`) {
      continue;
    }
    for (let j = 0; j < (extension?.extension?.length ?? 0); j++) {
      const internalExtension = extension.extension?.[j];
      if (internalExtension?.url === 'channelType' && internalExtension?.valueCoding?.code === code) {
        return extension as EncounterVirtualServiceExtension;
      }
    }
  }
  return null;
};

export const appointmentTypeForAppointment = (appointment: Appointment): AppointmentType => {
  // might as well default to walkin here
  // console.log('FHIR_APPOINTMENT_TYPE_MAP', FHIR_APPOINTMENT_TYPE_MAP, appointment.appointmentType?.text);
  return appointment.appointmentType?.text
    ? FHIR_APPOINTMENT_TYPE_MAP[appointment.appointmentType?.text] || 'walk-in'
    : 'walk-in';
};

export const appointmentAttendanceTypeAppointment = (
  appointment: Appointment
): AppointmentAttendanceType | undefined => {
  return appointment.meta?.tag?.find((tag) => tag.code === OTTEHR_MODULE.IP)
    ? 'in-person'
    : appointment.meta?.tag?.find((tag) => tag.code === OTTEHR_MODULE.TM)
    ? 'virtual'
    : undefined;
};

interface GetConsentAndRelatedDocRefsForAppointmentParams {
  appointmentId: string;
  patientId: string;
}
export interface GetConsentAndRelatedDocRefsForAppointmentResult {
  consents: Consent[] | undefined;
  docRefs: DocumentReference[] | undefined;
}

export const getConsentAndRelatedDocRefsForAppointment = async (
  input: GetConsentAndRelatedDocRefsForAppointmentParams,
  oystehr: Oystehr
): Promise<GetConsentAndRelatedDocRefsForAppointmentResult> => {
  const { appointmentId, patientId } = input;
  let docRefs: DocumentReference[] | undefined = undefined;
  let consents: Consent[] | undefined = undefined;
  console.log('searching for old consent doc refs');
  docRefs = (
    await oystehr.fhir.search<DocumentReference>({
      resourceType: 'DocumentReference',
      params: [
        {
          name: 'status',
          value: 'current',
        },
        {
          name: 'type',
          value: `${PAPERWORK_CONSENT_CODE_UNIQUE.system}|${PAPERWORK_CONSENT_CODE_UNIQUE.code}`,
        },
        {
          name: 'subject',
          value: `Patient/${patientId}`,
        },
        {
          name: 'related',
          value: `Appointment/${appointmentId}`,
        },
      ],
    })
  ).unbundle();
  if (docRefs?.[0]?.id) {
    console.log('searching for old consent resources');
    consents = (
      await oystehr.fhir.search<Consent>({
        resourceType: 'Consent',
        params: [
          { name: 'patient', value: `Patient/${patientId}` },
          { name: 'status', value: 'active' },
          { name: 'source-reference', value: `DocumentReference/${docRefs?.[0]?.id}` },
        ],
      })
    ).unbundle();
  }
  return { consents, docRefs };
};

export const getReasonForVisitFromAppointment = (appointment?: Appointment): string | undefined => {
  if (!appointment?.description) {
    return undefined;
  }
  const complaints = (appointment?.description ?? '').split(',');
  return complaints.map((complaint) => complaint.trim()).join(', ');
};

export const getReasonForVisitAndAdditionalDetailsFromAppointment = (
  appointment?: Appointment
): { reasonForVisit?: string; additionalDetails?: string } => {
  if (!appointment?.description) {
    return {};
  }
  const complaints = (appointment?.description ?? '').split(REASON_FOR_VISIT_SEPARATOR);

  return {
    reasonForVisit: complaints[0]?.trim(),
    additionalDetails: complaints[1]
      ?.trim()
      .split(',')
      .map((complaint) => complaint.trim())
      .join(', '),
  };
};

/** Code of the appointment's service category under SERVICE_CATEGORY_SYSTEM, if it carries one. */
export const getServiceCategoryCodeFromAppointment = (appointment?: Appointment): string | undefined =>
  getCoding(appointment?.serviceCategory, SERVICE_CATEGORY_SYSTEM)?.code;

export const isAppointmentWorkersComp = (appointment: Appointment): boolean =>
  getServiceCategoryCodeFromAppointment(appointment) === CODE_SYSTEM_SERVICE_CATEGORY_CODES['workers-comp'];

export const isAppointmentOccupationalMedicine = (appointment: Appointment): boolean =>
  getServiceCategoryCodeFromAppointment(appointment) === CODE_SYSTEM_SERVICE_CATEGORY_CODES['occupational-medicine'];

export const isAppointmentPreOp = (appointment: Appointment): boolean =>
  getServiceCategoryCodeFromAppointment(appointment) === CODE_SYSTEM_SERVICE_CATEGORY_CODES['pre-op'];

export const isAppointmentUrgentCare = (appointment: Appointment): boolean =>
  getServiceCategoryCodeFromAppointment(appointment) === CODE_SYSTEM_SERVICE_CATEGORY_CODES['urgent-care'];

export const isAppointmentAutoAccident = (appointment: Appointment): boolean => {
  const { reasonForVisit } = getReasonForVisitAndAdditionalDetailsFromAppointment(appointment);
  return isAppointmentUrgentCare(appointment) && reasonForVisit === 'Auto accident';
};

export const getCancellationReasonDisplay = (appointment?: Appointment): string | undefined => {
  if (!appointment?.cancelationReason?.coding?.[0]) {
    return undefined;
  }

  const coding = appointment.cancelationReason.coding[0];
  const baseDisplay = coding.display;
  const additionalInfo = coding.extension?.find(
    (ext) => ext.url === `${FHIR_ZAPEHR_URL}/StructureDefinition/cancellation-reason-additional-info`
  )?.valueString;

  if (additionalInfo) {
    return `${baseDisplay} - ${additionalInfo}`;
  }

  return baseDisplay;
};
